/**
 * Edible Store Pages
 * <store-status>: renders "Open Now • Closes at 5:00 PM" / "Closed • Opens at 10:00 AM Wednesday"
 * from a JSON hours object, evaluated in the store's own time zone.
 *
 * data-hours: {"mon":{"open":"10:00","close":"17:00"}, ..., "sun":null}
 * data-timezone: IANA zone, e.g. "America/New_York"
 */
(() => {
  const DAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  const toMinutes = (hhmm) => {
    const [h, m] = hhmm.split(':').map(Number);
    return h * 60 + (m || 0);
  };

  const formatTime = (hhmm) => {
    const [h, m] = hhmm.split(':').map(Number);
    const suffix = h >= 12 ? 'PM' : 'AM';
    const hour = h % 12 || 12;
    return `${hour}:${String(m || 0).padStart(2, '0')} ${suffix}`;
  };

  const nowInZone = (timeZone) => {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone, weekday: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    }).formatToParts(new Date());
    const get = (type) => parts.find((p) => p.type === type).value;
    const day = DAYS.indexOf(get('weekday').toLowerCase().slice(0, 3));
    return { day, minutes: Number(get('hour')) * 60 + Number(get('minute')) };
  };

  class StoreStatus extends HTMLElement {
    connectedCallback() {
      let hours;
      try { hours = JSON.parse(this.dataset.hours || ''); } catch (e) { return; }
      if (!hours) return;

      const labels = {
        open: this.dataset.labelOpen || 'Open Now',
        closed: this.dataset.labelClosed || 'Closed',
        closes: this.dataset.labelCloses || 'Closes at {time}',
        opens: this.dataset.labelOpens || 'Opens at {time}',
      };

      let now;
      try { now = nowInZone(this.dataset.timezone || undefined); } catch (e) { now = nowInZone(undefined); }

      const today = hours[DAYS[now.day]];
      let state; let detail;

      if (today && now.minutes >= toMinutes(today.open) && now.minutes < toMinutes(today.close)) {
        state = labels.open;
        detail = labels.closes.replace('{time}', formatTime(today.close));
      } else {
        state = labels.closed;
        // Find the next opening, starting later today, then up to a week ahead.
        for (let offset = 0; offset < 8; offset += 1) {
          const idx = (now.day + offset) % 7;
          const slot = hours[DAYS[idx]];
          if (!slot) continue;
          if (offset === 0 && now.minutes >= toMinutes(slot.open)) continue;
          const when = offset === 0 ? '' : ` ${DAY_NAMES[idx]}`;
          detail = labels.opens.replace('{time}', formatTime(slot.open)) + when;
          break;
        }
      }

      this.innerHTML = '';
      const strong = document.createElement('span');
      strong.className = 'store-status__state';
      strong.textContent = state;
      this.append(strong);
      if (detail) this.append(document.createTextNode(` • ${detail}`));
      this.hidden = false;
    }
  }

  if (!customElements.get('store-status')) customElements.define('store-status', StoreStatus);
})();

/**
 * <load-more-list>: reveals list items in batches.
 * All items are server-rendered (crawlable); items past the first batch are hidden once JS runs.
 *
 * data-batch-mobile / data-batch-desktop: items revealed per step (desktop = min-width 750px)
 * data-status-template: e.g. "Viewing 1-{shown} of {total}"
 * Children: [data-load-more-item], [data-load-more-button], [data-load-more-status], [data-load-more-footer]
 */
(() => {
  class LoadMoreList extends HTMLElement {
    connectedCallback() {
      this.items = [...this.querySelectorAll('[data-load-more-item]')];
      this.button = this.querySelector('[data-load-more-button]');
      this.status = this.querySelector('[data-load-more-status]');
      this.footer = this.querySelector('[data-load-more-footer]');
      this.mq = window.matchMedia('(min-width: 750px)');
      this.visible = this.batch();

      this.button.addEventListener('click', () => {
        const previous = this.visible;
        this.visible += this.batch();
        this.render();
        const firstNew = this.items[previous];
        if (firstNew) (firstNew.matches('a') ? firstNew : firstNew.querySelector('a'))?.focus();
      });
      this.mq.addEventListener('change', () => {
        this.visible = Math.max(this.visible, this.batch());
        this.render();
      });
      this.render();
    }

    batch() {
      const raw = this.mq.matches ? this.dataset.batchDesktop : this.dataset.batchMobile;
      return Math.max(1, Number(raw) || 5);
    }

    render() {
      const total = this.items.length;
      const shown = Math.min(this.visible, total);
      this.items.forEach((item, i) => { item.hidden = i >= shown; });
      if (this.status) {
        this.status.textContent = (this.dataset.statusTemplate || 'Viewing 1-{shown} of {total}')
          .replace('{shown}', shown).replace('{total}', total);
      }
      this.button.hidden = shown >= total;
      if (this.footer) this.footer.hidden = total <= this.batch();
    }
  }

  if (!customElements.get('load-more-list')) customElements.define('load-more-list', LoadMoreList);
})();
